import i18n from 'i18next';
import { PERMISSION_ACTION } from '@citylytics/domain/permissions/constants';
import { toast } from '@citylytics/ui-web/components/ui/toast';
import { createPermissionHooks } from '@citylytics/web-session/permissions/hooks';
import type { PermissionNotice } from '@citylytics/web-session/permissions/types';
import { createSessionService } from '@citylytics/web-session/session/service';
import { createSessionStore } from '@citylytics/web-session/session/store';
import { getQueryClient } from '../query-client/query-client.singleton';

// Created at load rather than behind a lazy getter: components read the store as a hook, which
// must be the same function on every render.
export const useAuthStore = createSessionStore({ onClear: () => getQueryClient().clear() });

const sessionService = createSessionService({ store: useAuthStore });

export const { requireAuth, loginRouteGuards, login, logout, leaveLostSession, submitNewPassword } =
  sessionService;

// Toasts fire from mutation callbacks, outside render, so they read i18next directly.
function notify(notice: PermissionNotice) {
  switch (notice.kind) {
    case 'refresh-failed':
      toast.add({
        type: 'warning',
        title: i18n.t('permissions.refreshFailed.title'),
        description: i18n.t('permissions.refreshFailed.description'),
      });

      return;
    case 'role-saved':
      toast.add({ type: 'success', title: i18n.t('roles.saved') });
  }
}

export const {
  useCurrentUserPermissions,
  useRoleOptions,
  useHoldsRole,
  useCanManageUser,
  useMunicipalityRoles,
  useMunicipalityRoleMutations,
  useMunicipalityRoleEditor,
  useMunicipalityLookup,
} = createPermissionHooks({
  store: useAuthStore,
  refreshCurrentUser: sessionService.refreshCurrentUser,
  // client-web changes users only through `/municipalities/{id}/users`, which takes both forms.
  userUpdateAccess: [
    PERMISSION_ACTION.USERS_UPDATE_GLOBAL,
    PERMISSION_ACTION.USERS_UPDATE_MUNICIPALITY,
  ],
  notify,
});
